import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Info, ArrowUpRight, ArrowDownRight, Minus, AlertTriangle, PlugZap,
  Settings2, Database, XCircle, CircleDashed,
} from "lucide-react";

// ============================================================
// KpiCard — the ONE KPI card component used across the platform.
// Implements Section 4.2 (KPI Card Behaviour) and Section 2.4
// (No Misleading Zero Values) of the scope spec.
//
// Never renders $0/0% unless state === 'no_records' (genuine zero).
// Every other unavailable state gets its own clear label.
// ============================================================

const STATE_META = {
  current:              { label: "Live",                  tone: "text-emerald-700", icon: null,        chip: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  stale:                { label: "Stale",                 tone: "text-amber-700",  icon: CircleDashed, chip: "bg-amber-50 text-amber-700 border-amber-200" },
  partial:              { label: "Partial data",          tone: "text-amber-700",  icon: Database,     chip: "bg-amber-50 text-amber-700 border-amber-200" },
  awaiting_integration: { label: "Awaiting integration",  tone: "text-slate-500",  icon: PlugZap,      chip: "bg-slate-100 text-slate-700 border-slate-200" },
  not_configured:       { label: "Calculation not configured", tone: "text-slate-500", icon: Settings2, chip: "bg-slate-100 text-slate-600 border-slate-200" },
  no_records:           { label: "No matching records",   tone: "text-slate-500",  icon: null,         chip: "bg-slate-50 text-slate-500 border-slate-200" },
  error:                { label: "Calculation error",     tone: "text-rose-700",   icon: XCircle,      chip: "bg-rose-50 text-rose-700 border-rose-200" },
};

const TL_DOT = { green: "bg-emerald-500", amber: "bg-amber-500", red: "bg-rose-500", grey: "bg-slate-300" };

function formatValue(value, unit) {
  if (value == null) return null;
  if (unit === "currency") return "$" + Number(value).toLocaleString(undefined, { maximumFractionDigits: 0 });
  if (unit === "percent")  return Number(value).toFixed(1) + "%";
  if (unit === "days")     return `${Math.round(Number(value))}d`;
  return Number(value).toLocaleString();
}

export function KpiCard({ metricId, testId, onClick }) {
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/metrics/resolve/${metricId}`)
      .then((r) => { if (alive) setPayload(r.data); })
      .catch((e) => { if (alive) setPayload({ state: "error", error: e.message, metric_id: metricId, metric_name: metricId }); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [metricId]);

  if (loading) return <div data-testid={testId} className="rounded-lg border border-slate-200 bg-white p-4 h-28 shimmer" />;
  if (!payload) return null;

  const state = payload.state || "not_configured";
  const meta = STATE_META[state] || STATE_META.not_configured;
  const Icon = meta.icon;
  const valueStr = state === "current" ? formatValue(payload.value, payload.unit) : (state === "no_records" ? formatValue(0, payload.unit) : null);
  const target = payload.target;
  const variance = payload.variance;
  const variancePct = payload.variance_pct;
  const trafficLight = payload.traffic_light || "grey";

  const trendIcon = variance == null ? Minus : variance > 0 ? ArrowUpRight : ArrowDownRight;
  const TrendI = trendIcon;
  const trendTone = variance == null ? "text-slate-400" : (variance > 0 ? "text-emerald-700" : "text-rose-700");

  return (
    <div
      data-testid={testId || `kpi-${payload.metric_id}`}
      onClick={onClick}
      className={`rounded-lg border border-slate-200 bg-white p-4 transition-all ${onClick ? "cursor-pointer hover:border-gbe hover:shadow-md" : ""}`}
    >
      <div className="flex items-start justify-between mb-1">
        <div className="text-[11px] uppercase tracking-wider text-slate-500 leading-tight">
          {payload.metric_name}
        </div>
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger className="text-slate-300 hover:text-slate-500">
              <Info className="w-3.5 h-3.5" />
            </TooltipTrigger>
            <TooltipContent side="left" className="max-w-xs text-xs space-y-1">
              <div className="font-semibold">{payload.metric_name}</div>
              <div className="text-slate-600">{payload.description}</div>
              {payload.business_definition && (
                <div><span className="text-slate-400">Definition: </span>{payload.business_definition}</div>
              )}
              {payload.calculation && (
                <div className="font-mono text-[10px] text-slate-500">{payload.calculation}</div>
              )}
              <div className="pt-1 border-t border-slate-100">
                <span className="text-slate-400">Source: </span><span className="font-mono">{payload.source_system}</span>
              </div>
              <div><span className="text-slate-400">Refresh: </span>{payload.refresh_frequency}</div>
              <div><span className="text-slate-400">Owner: </span>{payload.metric_owner || "—"}</div>
              {payload.drill_down_path && (
                <div><span className="text-slate-400">Drill: </span>{payload.drill_down_path}</div>
              )}
              <div className="text-[10px] text-slate-400">ID {payload.metric_id} · v{payload.version || 1}</div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      {/* Value row */}
      <div className="flex items-baseline gap-2">
        {valueStr != null ? (
          <>
            <span className={`w-2 h-2 rounded-full ${TL_DOT[trafficLight]}`} aria-hidden />
            <span className="font-display text-2xl text-slate-900 leading-none">{valueStr}</span>
          </>
        ) : (
          <span className={`text-sm font-medium ${meta.tone}`}>
            {Icon && <Icon className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />}
            {meta.label}
          </span>
        )}
      </div>

      {/* Sub-row: variance / target / state chip */}
      <div className="mt-2 flex items-center justify-between text-xs">
        <div className="flex items-center gap-2 text-slate-500 min-w-0">
          {valueStr != null && target != null && (
            <>
              <TrendI className={`w-3.5 h-3.5 ${trendTone}`} />
              <span className={trendTone}>
                {variancePct != null && (variancePct >= 0 ? "+" : "")}{variancePct?.toFixed(1)}%
              </span>
              <span className="text-slate-400">vs target {formatValue(target, payload.unit)}</span>
            </>
          )}
          {valueStr != null && target == null && payload.comparison?.value != null && (() => {
            const cv = payload.comparison.variance_pct;
            const CTrend = cv == null ? Minus : cv > 0 ? ArrowUpRight : ArrowDownRight;
            const cTone = cv == null ? "text-slate-400" : (cv > 0 ? "text-emerald-700" : "text-rose-700");
            return (
              <>
                <CTrend className={`w-3.5 h-3.5 ${cTone}`} />
                {cv != null && <span className={cTone}>{cv >= 0 ? "+" : ""}{cv.toFixed(1)}%</span>}
                <span className="text-slate-400 truncate">
                  vs {payload.comparison.label} {formatValue(payload.comparison.value, payload.unit)}
                </span>
              </>
            );
          })()}
        </div>
        <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${meta.chip}`}>
          {meta.label}
        </Badge>
      </div>
    </div>
  );
}

// Data-confidence pill used at the top of dashboards (Section 28.5)
export function DataConfidenceBar({ metricIds }) {
  const [payloads, setPayloads] = useState([]);
  useEffect(() => {
    if (!metricIds?.length) return;
    api.post("/metrics/resolve-batch", metricIds).then((r) => setPayloads(r.data)).catch(() => {});
  }, [JSON.stringify(metricIds)]);

  const counts = { high: 0, medium: 0, low: 0, not_calculated: 0 };
  payloads.forEach((p) => { counts[p.confidence || "not_calculated"] = (counts[p.confidence || "not_calculated"] || 0) + 1; });
  const total = payloads.length || 1;

  return (
    <div data-testid="data-confidence-bar" className="rounded-lg border border-slate-200 bg-white p-3 flex items-center gap-4 text-xs">
      <div className="text-slate-500 uppercase tracking-wider text-[10px]">Data Confidence</div>
      <div className="flex-1 h-2 rounded-full overflow-hidden bg-slate-100 flex">
        <div className="bg-emerald-500" style={{ width: `${(counts.high / total) * 100}%` }} />
        <div className="bg-amber-500"    style={{ width: `${(counts.medium / total) * 100}%` }} />
        <div className="bg-rose-500"     style={{ width: `${(counts.low / total) * 100}%` }} />
        <div className="bg-slate-300"    style={{ width: `${(counts.not_calculated / total) * 100}%` }} />
      </div>
      <div className="flex items-center gap-3">
        <Legend dot="bg-emerald-500" label={`${counts.high} live`} />
        <Legend dot="bg-amber-500"   label={`${counts.medium} partial`} />
        <Legend dot="bg-rose-500"    label={`${counts.low} awaiting`} />
        <Legend dot="bg-slate-300"   label={`${counts.not_calculated} n/a`} />
      </div>
    </div>
  );
}

function Legend({ dot, label }) {
  return <span className="inline-flex items-center gap-1 text-slate-600"><span className={`w-2 h-2 rounded-full ${dot}`} />{label}</span>;
}
